/**
 * Managed card template renderer.
 *
 * Fills data-element slots in a pre-built HTML template with Algolia hit data,
 * applying the content mapping and style variables saved in the card_template
 * view setting.
 *
 * Custom template path (old {hit.field} style) is a separate concern — see
 * algolia-hits-component.js for the branch point.
 */

import { __ } from './i18n.js';

/**
 * Returns an SVG <use> string referencing the TUI sprite (inlined in wp_footer).
 * Icon names follow the "category/slug" convention, e.g. "system/check".
 * Symbol IDs in the sprite are "tui-{category}-{slug}".
 */
function iconToSvg( name ) {
	if ( ! name ) return '';
	const symbolId = 'tui-' + name.replace( '/', '-' );
	return `<svg class="tgbl-meta-icon" aria-hidden="true" focusable="false" width="1em" height="1em" viewBox="0 0 24 24"><use href="#${ symbolId }"></use></svg>`;
}

const templateCache = new Map();

const CSS_VAR_MAP = {
	bg_color:            '--tgbl-card-bg',
	accent_color:        '--tgbl-card-accent',
	accent_light_color:  '--tgbl-card-accent-light',
	text_color:          '--tgbl-card-text',
	text_emphasis_color: '--tgbl-card-text-emphasis',
	text_on_accent:      '--tgbl-card-text-on-accent',
	font_heading:        '--tgbl-card-font-heading',
	font_normal:         '--tgbl-card-font-normal',
	font_small:          '--tgbl-card-font-small',
	shadow_x:            '--tgbl-card-shadow-x',
	shadow_y:            '--tgbl-card-shadow-y',
	shadow_blur:         '--tgbl-card-shadow-blur',
	shadow_spread:       '--tgbl-card-shadow-spread',
	shadow_color:        '--tgbl-card-shadow-color',
};

/**
 * Returns true when a toggle value is on.
 * Defaults to true when the key has never been set (first load shows all slots).
 */
function toggleOn( content, key ) {
	return key in content ? Boolean( content[ key ] ) : true;
}

/**
 * CSS-class-safe slug of a mapped hit value, so themes can style the icon
 * per type (e.g. .icon--video { background: … }).
 */
function classSlug( value ) {
	return String( value ).toLowerCase().replace( /[^a-z0-9]+/g, '-' ).replace( /^-+|-+$/g, '' );
}

/**
 * Resolves one icon config section (prefix = element id for the primary
 * section, '{id}_fallback' for the fallback section): a mapping-row match on
 * the section's datapoint wins, else the section's static icon/image.
 *
 * Returns { type: 'icon'|'image', icon?, src?, rawValue } or null.
 */
function resolveIconSection( hit, content, prefix ) {
	const datapoint = content[ prefix + '_datapoint' ] || '';
	const rawValue  = datapoint ? String( hit[ datapoint ] ?? '' ) : '';

	if ( datapoint && rawValue !== '' ) {
		let mappings;
		try { mappings = JSON.parse( content[ prefix + '_mappings' ] || '[]' ); } catch { mappings = []; }
		if ( ! Array.isArray( mappings ) ) mappings = [];

		const match = mappings.find( m =>
			m && m.value === rawValue
			&& ( ( m.type || 'icon' ) === 'image' ? m.image_url : m.icon )
		);
		if ( match ) {
			return ( match.type || 'icon' ) === 'image'
				? { type: 'image', src: match.image_url, rawValue }
				: { type: 'icon', icon: match.icon, rawValue };
		}
	}

	const staticType = content[ prefix + '_icon_type' ] || 'icon';
	if ( staticType === 'image' && content[ prefix + '_icon_image_url' ] ) {
		return { type: 'image', src: content[ prefix + '_icon_image_url' ], rawValue };
	}
	if ( staticType === 'icon' && content[ prefix + '_icon_name' ] ) {
		return { type: 'icon', icon: content[ prefix + '_icon_name' ], rawValue };
	}
	return null;
}

/**
 * Resolves the icon element's visual: the primary section first (mapping
 * match, else its static icon), then — when the primary resolves nothing —
 * the fallback section ({id}_fallback_*, its own mapping + static icon).
 *
 * Returns { type: 'icon'|'image', icon?, src?, rawValue } or null when
 * nothing resolves (the slot is then hidden).
 */
function resolveIconVisual( hit, content, id ) {
	const primary = resolveIconSection( hit, content, id );
	if ( primary ) return primary;

	if ( toggleOn( content, id + '_fallback_toggle' ) ) {
		return resolveIconSection( hit, content, id + '_fallback' );
	}
	return null;
}

/**
 * Parses and caches the template HTML. Returns the root element.
 */
function getTemplateRoot( templateHtml ) {
	if ( ! templateCache.has( templateHtml ) ) {
		const div       = document.createElement( 'div' );
		div.innerHTML   = templateHtml;
		templateCache.set( templateHtml, div.firstElementChild );
	}
	return templateCache.get( templateHtml );
}

const DIMENSION_KEYS = new Set( [
	'font_heading', 'font_normal', 'font_small',
	'shadow_x', 'shadow_y', 'shadow_blur', 'shadow_spread',
] );

function resolveDimensionValue( v ) {
	try {
		const parsed = JSON.parse( v );
		if ( parsed !== null && typeof parsed === 'object' && 'value' in parsed ) {
			return `${ parsed.value }${ parsed.unit }`;
		}
	} catch {}
	const n = parseFloat( v );
	return isNaN( n ) ? v : `${ n }px`;
}

/**
 * Builds an inline style string of CSS custom properties from the styles object.
 */
function buildStyleAttr( styles ) {
	return Object.entries( CSS_VAR_MAP )
		.filter( ( [ key ] ) => styles[ key ] )
		.map( ( [ key, varName ] ) => {
			const val = DIMENSION_KEYS.has( key )
				? resolveDimensionValue( styles[ key ] )
				: styles[ key ];
			return `${ varName }: ${ val }`;
		} )
		.join( '; ' );
}

/**
 * Fills the cloned card DOM with hit data according to the content mapping.
 */
function fillCard( card, hit, content, facetMappingsByAttribute = {} ) {
	card.querySelectorAll( '[data-element]' ).forEach( el => {
		const id  = el.dataset.element;
		const tag = el.tagName.toLowerCase();

		if ( id === 'root' ) return;

		const isOn = toggleOn( content, id + '_toggle' );

		if ( ! isOn ) {
			el.style.display = 'none';
			return;
		}

		// Icon slot: static or value-mapped type icon. Hidden entirely when
		// nothing resolves, so cards without icon config stay unchanged.
		if ( id === 'icon' ) {
			const visual = resolveIconVisual( hit, content, id );
			if ( ! visual ) {
				el.style.display = 'none';
				return;
			}
			if ( visual.type === 'image' ) {
				const img = document.createElement( 'img' );
				img.src = visual.src;
				img.alt = '';
				el.innerHTML = '';
				el.appendChild( img );
			} else {
				el.innerHTML = iconToSvg( visual.icon );
			}
			// Per-value modifier class so themes can style the icon per type.
			if ( visual.rawValue !== '' ) {
				el.classList.add( `${ el.className.split( ' ' )[ 0 ] || 'icon' }--${ classSlug( visual.rawValue ) }` );
			}
			return;
		}

		// Container elements (those with child data-element slots) only need
		// toggle handling — their children populate themselves.
		// Exception: ul/ol containers with a _datapoint are "dynamic list" elements
		// (e.g. tag cloud) — populate their <li> children from the hit array value.
		if ( el.querySelector( '[data-element]' ) ) {
			if ( tag === 'ul' || tag === 'ol' ) {
				const datapoint = content[ id + '_datapoint' ] || '';
				const rawVal    = datapoint ? hit[ datapoint ] : undefined;
				const values    = Array.isArray( rawVal )
					? rawVal.map( v => String( v ) ).filter( Boolean )
					: [];

				// The template's <li>s are PLACEHOLDERS — they exist only to carry the styling and to
				// give the editor sample chips to show (assets/src/hits-templates/template.html ships
				// two for the tag cloud, "WordPress" and "JavaScript", and one for badges). They must
				// always be cleared, whether or not there is anything to replace them with.
				//
				// This block used to do nothing unless `values.length`, so a record with no tags kept
				// the placeholders and rendered them as empty coloured pills — two of them for the tag
				// cloud, which is exactly the count of placeholders in the template. Reported
				// 2026-08-03. Four paths reached that state: the attribute missing from the record, a
				// non-array value, an array of only empty strings, and no datapoint configured at all.
				const liTemplate = el.querySelector( 'li' );
				const liClass    = liTemplate ? liTemplate.className : '';
				Array.from( el.querySelectorAll( 'li' ) ).forEach( li => li.remove() );

				// Nothing to show: hide the whole list rather than leave an empty one, matching the icon
				// slot above ("hidden entirely when nothing resolves") so the list contributes no gap.
				if ( ! values.length ) {
					el.style.display = 'none';
					return;
				}

				values.forEach( val => {
					const li       = document.createElement( 'li' );
					li.className   = liClass;
					li.textContent = val;
					el.appendChild( li );
				} );
			}
			return;
		}

		let datapoint = content[ id + '_datapoint' ] || '';

		// Img / li elements: fall back to parent container's datapoint key for
		// configs saved before the key was corrected to use the child's own id.
		if ( ! datapoint && ( tag === 'img' || tag === 'li' ) ) {
			const parentId = el.parentElement?.closest( '[data-element]' )?.dataset.element;
			if ( parentId ) datapoint = content[ parentId + '_datapoint' ] || '';
		}

		let value;
		if ( datapoint === 'custom' ) {
			let parts;
			try { parts = JSON.parse( content[ id + '_custom_content' ] || '[]' ); } catch { parts = []; }
			if ( ! Array.isArray( parts ) ) parts = [];
			value = parts.map( p => {
				if ( p.type === 'text' ) return p.value || '';
				if ( ! p.field ) return '';
				const raw    = String( hit[ p.field ] ?? '' );
				const fMap   = facetMappingsByAttribute[ p.field ];
				return ( fMap && fMap[ raw ] != null ) ? fMap[ raw ] : raw;
			} ).join( '' );
		} else {
			const rawValue = datapoint ? String( hit[ datapoint ] ?? '' ) : '';
			const attrMap  = datapoint ? facetMappingsByAttribute[ datapoint ] : null;
			value          = ( attrMap && attrMap[ rawValue ] != null ) ? attrMap[ rawValue ] : rawValue;
		}

		if ( tag === 'img' ) {
			const useStatic = ( content[ 'image_source' ] || 'indexed_data' ) === 'static_image';
			const src       = useStatic
				? ( content[ 'image_fallback_url' ] || '' )
				: ( value || content[ 'image_fallback_url' ] || '' );
			el.setAttribute( 'src', src );

		} else if ( tag === 'button' ) {
			// Convert to an anchor for proper link behaviour.
			// Saved labels arrive already translated (server-side, via the
			// translation provider); only the default falls back to gettext here.
			const label   = content[ id + '_label' ] || __( 'Learn More', 'tangible-searchsync' );
			const linkKey = content[ id + '_link' ] || 'post_link';
			// 'post_link' is the sentinel for the default permalink fallback chain.
			// Any other value is treated as a hit attribute key.
			const postLink = linkKey === 'post_link'
				? ( hit.post_link || hit.permalink || hit.url || '#' )
				: ( String( hit[ linkKey ] ?? '' ) || hit.post_link || hit.permalink || hit.url || '#' );
			const a        = document.createElement( 'a' );
			a.className    = el.className || 'cta';
			a.dataset.element = id;
			a.textContent  = label;
			a.href         = postLink;
			el.replaceWith( a );

		} else if ( value === '' ) {
			// No value for this record: hide the slot instead of rendering an empty one.
			//
			// Visible in the same 2026-08-03 report as the empty tag pills: a <dd> meta item whose
			// datapoint resolved to '' still drew its configured icon below, so a card missing (say)
			// a delivery-mode value showed a bare clock glyph with no text after it. An empty title or
			// description likewise left a box contributing its own margin.
			//
			// Safe for elements with no datapoint configured too: `el.textContent = value` below would
			// have blanked whatever static text the template shipped anyway, so there is nothing to
			// preserve by leaving them visible. Presence is governed by the `_toggle` check at the top
			// of this function; this only suppresses slots that are on but have nothing to say.
			el.style.display = 'none';

		} else {
			el.textContent = value;

			if ( tag === 'dd' ) {
				const iconType = content[ id + '_icon_type' ] || 'icon';
				const iconName = iconType === 'icon'  ? ( content[ id + '_icon_name'      ] || '' ) : '';
				const imageUrl = iconType === 'image' ? ( content[ id + '_icon_image_url' ] || '' ) : '';

				if ( iconName || imageUrl ) {
					const text = el.textContent;
					el.innerHTML = '';

					if ( iconName ) {
						const wrap = document.createElement( 'span' );
						wrap.className = 'tgbl-meta-icon';
						wrap.innerHTML = iconToSvg( iconName );
						el.appendChild( wrap );
					} else if ( imageUrl ) {
						const img = document.createElement( 'img' );
						img.src       = imageUrl;
						img.alt       = '';
						img.className = 'tgbl-meta-icon-img';
						el.appendChild( img );
					}

					const textEl = document.createElement( 'span' );
					textEl.textContent = text;
					el.appendChild( textEl );
				}
			}
		}
	} );

	// Hide the metadata dl when all its children are hidden.
	const meta = card.querySelector( '[data-element="metadata"]' );
	if ( meta ) {
		const anyVisible = Array.from( meta.querySelectorAll( '[data-element]' ) )
			.some( child => child.style.display !== 'none' );
		if ( ! anyVisible ) meta.style.display = 'none';
	}
}

/**
 * Renders a hit card using the managed slot-based template.
 *
 * Returns an htm/Preact element suitable for the InstantSearch `templates.item`
 * callback. The card HTML is injected via dangerouslySetInnerHTML to preserve
 * the full template markup (including CSS classes, data attributes, etc.).
 *
 * @param {Object}   hit          Algolia hit object.
 * @param {Object}   cardTemplate Parsed card_template JSON { template, content, styles }.
 * @param {string}   templateHtml Raw HTML of the template file.
 * @param {Function} html         InstantSearch htm tagged-template function.
 */
/**
 * For inline cards, wraps every direct child except the image in a div.card-body so
 * the image and content area can be laid out as a flex row without each content
 * element becoming its own horizontal flex item.
 */
function wrapInlineCardBody( card ) {
	const image = card.querySelector( ':scope > .image' );
	const body  = document.createElement( 'div' );
	body.className = 'card-body';
	Array.from( card.children ).forEach( child => {
		if ( child !== image ) body.appendChild( child );
	} );
	card.appendChild( body );
}

export function renderManagedCardTemplate( hit, cardTemplate, templateHtml, html, facetMappingsByAttribute = {} ) {
	const { content = {}, styles = {} } = cardTemplate;

	const root = getTemplateRoot( templateHtml );
	const card = root.cloneNode( true );

	fillCard( card, hit, content, facetMappingsByAttribute );

	if ( card.dataset.layout === 'inline' ) {
		wrapInlineCardBody( card );
	}

	const styleAttr = buildStyleAttr( styles );
	if ( styleAttr ) {
		card.setAttribute( 'style', ( card.getAttribute( 'style' ) || '' ) + styleAttr );
	}

	const rendered = card.outerHTML;

	return html`<div class="tgbl-ss-card-wrap" dangerouslySetInnerHTML=${{ __html: rendered }}></div>`;
}
